<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

// iOS reuses the editor with its own endpoints and hint; desktop is the default.
const props = defineProps<{ version: string, notesPath?: string, savePath?: string, hint?: string }>()
const notesPath = () => props.notesPath ?? `client/desktop-release-notes/${encodeURIComponent(props.version)}`
const savePath = () => props.savePath ?? `admin/desktop-release-notes/${encodeURIComponent(props.version)}`
const emit = defineEmits<{ saved: [] }>()
const notes = ref('')
const notesEn = ref('')
const revision = ref<string | null>(null)
const loading = ref(true)
const saving = ref(false)
const translating = ref(false)
const { translateText } = useTranslationTools()
const error = ref('')
const saved = ref(false)
const original = ref('')
const snapshot = computed(() => JSON.stringify([notes.value, notesEn.value]))
const dirty = computed(() => snapshot.value !== original.value)
const api = useAPI()
type NotesRecord = { changelog: string, changelog_en: string, revision: string | null }

function accept(data: NotesRecord) {
  notes.value = data.changelog
  notesEn.value = data.changelog_en
  revision.value = data.revision
  original.value = snapshot.value
}

async function load() {
  loading.value = true
  error.value = ''
  saved.value = false
  try {
    accept(await api.get(notesPath()) as NotesRecord)
  } catch (cause: any) {
    error.value = cause?.data?.message || cause?.message || String(cause)
  } finally {
    loading.value = false
  }
}

async function refresh() {
  // Other editors can save this version while the CI promotion editor stays mounted.
  if (loading.value || saving.value || translating.value || dirty.value) return
  await load()
}

async function save(): Promise<boolean> {
  if (loading.value || saving.value || translating.value || !original.value) return false
  if (!dirty.value) return true
  saving.value = true
  saved.value = false
  error.value = ''
  try {
    accept(await api.put(savePath(), {
      body: { changelog: notes.value, changelog_en: notesEn.value, revision: revision.value },
    }) as NotesRecord)
    saved.value = true
    emit('saved')
    return true
  } catch (cause: any) {
    error.value = cause?.status === 409 || cause?.statusCode === 409
      ? '此版本的更新说明已被修改，请重新加载后再编辑。'
      : cause?.data?.message || cause?.message || String(cause)
    return false
  } finally {
    saving.value = false
  }
}

async function translateNotesEn() {
  if (loading.value || saving.value || translating.value || !original.value || !notes.value.trim()) return
  translating.value = true
  error.value = ''
  try {
    if (notesEn.value.trim()) {
      const confirmed = await useDialog().confirm({
        title: '覆盖英文更新说明？',
        body: '已有英文更新说明，继续翻译会覆盖当前内容。',
        confirmButtonText: '覆盖并翻译',
        cancelButtonText: '取消',
        confirmButtonVariant: 'warning',
      })
      if (confirmed !== 'confirm') return
    }
    const result = await translateText(notes.value, {
      customPrompt: `Translate these desktop app release notes from Chinese to natural English.

Keep the same bullet/list structure.
Preserve version numbers, product names, technical terms, URLs, and code-like text.
Use concise app-store style wording.
Return ONLY the translated release notes without any explanation.`,
    })
    if (!result.success || !result.translatedText?.trim()) throw new Error('翻译未返回内容')
    notesEn.value = result.translatedText
    saved.value = false
  } catch (cause: any) {
    error.value = `翻译失败：${cause?.data?.message || cause?.message || String(cause)}`
  } finally {
    translating.value = false
  }
}

onMounted(load)
defineExpose({ save, refresh })
</script>

<template lang="pug">
div(class="space-y-3")
  p(class="text-sm font-medium") 更新说明 · {{ version }}
  p(class="text-xs text-muted-foreground") {{ hint ?? '同一版本的 macOS、Windows 和各发布渠道共用；留空则不显示。' }}
  p(v-if="loading" class="text-sm text-muted-foreground") 正在加载更新说明...
  div(class="grid items-start gap-4 md:grid-cols-2")
    label(class="grid gap-1.5 text-sm")
      span(class="flex h-8 items-center") 中文
      Textarea(v-model="notes" :disabled="loading || saving || translating || !original" :maxlength="20000" rows="7" placeholder="- 修复...\n- 改进...")
    label(class="grid gap-1.5 text-sm")
      span(class="flex h-8 items-center justify-between gap-2")
        span English
        Button(type="button" size="sm" variant="outline" class="h-8" :aria-label="translating ? '翻译中' : '自动翻译'" :disabled="loading || saving || translating || !original || !notes.trim()" @click.prevent="translateNotesEn")
          Icon(name="material-symbols:translate" class="mr-1 h-4 w-4")
          | {{ translating ? '翻译中...' : '自动翻译' }}
      Textarea(v-model="notesEn" :disabled="loading || saving || translating || !original" :maxlength="20000" rows="7" placeholder="- Fixed...\n- Improved...")
  p(v-if="error" class="text-sm text-destructive" role="alert") {{ error }}
  div(class="flex flex-col items-center gap-3")
    Button(type="button" variant="outline" class="w-full" :disabled="loading || saving || translating || !original || !dirty" @click="save") {{ saving ? '保存中...' : '保存更新说明' }}
    Button(v-if="error" type="button" size="sm" variant="ghost" :disabled="loading || saving || translating" @click="load") 重新加载
    span(v-if="saved && !dirty" class="text-xs text-muted-foreground" role="status") 已保存
</template>
